//src/app/services/questionnaires.ts
import { createApi } from '@reduxjs/toolkit/query/react';
import { IPrepareQuestionnaireState } from 'app/redux/store';
import { staggeredBaseQueryWithBailOut } from 'utils';
import { Questionnaire, TQuestionnaireData } from './responseTypes/index';

interface ICreateQuestionnaireRequest {
  project: {
    service: 'tax';
    business_ids?: number[];
    project_beneficiary_attributes?: {
      beneficiary_type: string | null;
      beneficiary_id: number | null;
    } | null;
  };
  questionnaire: {
    year: string | null;
    fbar_only: boolean;
    data: {
      prepareQuestionnaire: IPrepareQuestionnaireState;
    };
  };
}
export const questionnairesApi = createApi({
  reducerPath: 'questionnairesApi',
  baseQuery: staggeredBaseQueryWithBailOut(`${process.env.REACT_APP_API_URL}`),
  tagTypes: ['Questionnaires', 'Questionnaire', 'Years'],
  endpoints: (builder) => ({
    getQuestionnaireById: builder.query<
      { questionnaire: Questionnaire },
      string
    >({
      query: (id: string) => `projects/${id}/questionnaire`,
      providesTags: () => [{ type: 'Questionnaire' }],
      transformResponse: ({
        questionnaire,
      }: {
        questionnaire: Questionnaire;
      }): {
        questionnaire: Questionnaire;
      } => {
        return {
          questionnaire: {
            ...questionnaire,
            data: {
              taxQuestionnaire: questionnaire.data.taxQuestionnaire,
              prepareQuestionnaire: {
                // @ts-ignore
                ...questionnaire.data.configureLifeIncome,
                ...questionnaire.data.prepareQuestionnaire,
              },
            },
          },
        };
      },
    }),
    updateQuestionnaire: builder.mutation({
      query: ({
        id,
        questionnaire,
      }: {
        id: string;
        questionnaire: { data: TQuestionnaireData };
      }) => ({
        url: `projects/${id}/questionnaire`,
        method: 'PATCH',
        body: {
          questionnaire,
        },
      }),
      invalidatesTags: [{ type: 'Questionnaires', id: 'LIST' }],
    }),
    createQuestionnaire: builder.mutation({
      query: (body: ICreateQuestionnaireRequest) => ({
        url: `/projects`,
        method: 'POST',
        body,
      }),
      invalidatesTags: [
        { type: 'Questionnaires', id: 'LIST' },
        { type: 'Years', id: 'LIST' },
      ],
    }),
    deleteQuestionnaire: builder.mutation({
      query: (id: string) => ({
        url: `/projects/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: [
        { type: 'Questionnaires', id: 'LIST' },
        { type: 'Years', id: 'LIST' },
      ],
    }),
    getYearsHaveQuestionnaires: builder.query<{ years: string[] }, void>({
      query: () => `/projects/years`,
      providesTags: () => [{ type: 'Years', id: 'LIST' }],
    }),
  }),
});

export const {
  useGetQuestionnaireByIdQuery,
  useCreateQuestionnaireMutation,
  useUpdateQuestionnaireMutation,
  useDeleteQuestionnaireMutation,
  useGetYearsHaveQuestionnairesQuery,
} = questionnairesApi;

export const useGetQuestionnaireByIdQueryState =
  questionnairesApi.endpoints.getQuestionnaireById.useQueryState;
